import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { createBooking, getBookingById } from "../api/bookings";

function BookingConfirm() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");

  const [booking, setBooking] = useState(state?.booking || null);
  const [loading, setLoading] = useState(Boolean(bookingId && !state?.booking));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (bookingId) {
      getBookingById(bookingId)
        .then((res) => setBooking(res.data || res))
        .catch(() => setError("Unable to load booking details."))
        .finally(() => setLoading(false));
    } else if (!state) {
      navigate("/");
    }
  }, [bookingId, state, navigate]);

  const selectedBooth = state?.booth;
  const selectedDate = state?.date;
  const selectedTime = state?.time;
  const selectedDuration = state?.duration || 1;
  const legacySelectedAddons = state?.selected_addons || [];

  const formatMoney = (value) => {
    if (value == null) return "$0.00";
    const n = Number(value);
    if (Number.isNaN(n)) return `$${value}`;
    return `$${n.toFixed(2)}`;
  };

  const {
    totalAmount,
    baseAmount,
    addonsTotal,
    selectedAddons,
  } = useMemo(() => {
    let total = 0;
    let base = 0;
    let addons = 0;
    let addonsList = [];

    if (booking) {
      const rawTotal =
        booking.total_amount ?? booking.total ?? booking.totalAmount ?? 0;

      const backendAddonsTotal =
        booking.addons_total_amount ?? booking.addonsTotal ?? null;

      const list =
        booking.selected_addons ||
        booking.selectedAddons ||
        [];

      addonsList = Array.isArray(list) ? list : [];

      if (typeof backendAddonsTotal === "number") {
        addons = backendAddonsTotal;
      } else if (addonsList.length > 0) {
        addons = addonsList.reduce((sum, a) => {
          if (!a) return sum;
          const lt = Number(a.line_total ?? a.lineTotal ?? 0);
          if (!Number.isNaN(lt)) return sum + lt;
          const qty = Number(a.quantity ?? 0);
          const up = Number(a.unit_price ?? a.unitPrice ?? 0);
          if (Number.isNaN(qty) || Number.isNaN(up)) return sum;
          return sum + qty * up;
        }, 0);
      }

      total = rawTotal || addons;
      base = total - addons;
    } else {
      // Legacy path: not yet created booking, just estimate base total
      const pricePerHour = Number(
        selectedBooth?.price_per_hour ??
          selectedBooth?.price ??
          0
      );
      base = pricePerHour * selectedDuration;
      total = base;
      addons = 0;
      addonsList = [];
    }

    return {
      totalAmount: total,
      baseAmount: base,
      addonsTotal: addons,
      selectedAddons: addonsList,
    };
  }, [booking, selectedBooth?.price_per_hour, selectedBooth?.price, selectedDuration]);

  const startTimeDisplay = useMemo(() => {
    if (booking?.start_time)
      return new Date(booking.start_time).toLocaleString();
    if (selectedDate && selectedTime) return `${selectedDate} at ${selectedTime}`;
    return "";
  }, [booking?.start_time, selectedDate, selectedTime]);

  const endTimeDisplay = useMemo(() => {
    if (booking?.end_time) return new Date(booking.end_time).toLocaleString();
    return "";
  }, [booking?.end_time]);

  const boothName =
    booking?.booth_name ||
    booking?.booth?.booth_name ||
    selectedBooth?.booth_name ||
    "Booth";

  const salonName =
    booking?.salon_name ||
    booking?.booth?.salon?.name ||
    selectedBooth?.salon_name ||
    "Salon";

  const createFromLegacyState = async () => {
    if (!selectedBooth || !selectedDate || !selectedTime) {
      setError("No booking details found.");
      return;
    }
    const to24Hour = (timeStr) => {
      if (!timeStr) return "";
      const parsed = new Date(`01/01/2024 ${timeStr}`);
      const hours = parsed.getHours().toString().padStart(2, "0");
      const minutes = parsed.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const timeValue = selectedTime.includes(":")
      ? to24Hour(selectedTime)
      : selectedTime;
    const start = new Date(`${selectedDate}T${timeValue}`);
    if (Number.isNaN(start.getTime())) {
      setError("Invalid date or time.");
      return;
    }
    const end = new Date(start.getTime() + selectedDuration * 60 * 60 * 1000);

    try {
      setSubmitting(true);
      const payload = {
        booth_id: selectedBooth.id,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
      };

      // If we came here with add-ons from a legacy state, preserve them
      if (Array.isArray(legacySelectedAddons) && legacySelectedAddons.length) {
        payload.selected_addons = legacySelectedAddons;
      }

      const res = await createBooking(payload);
      const created =
        res.data ||
        res; // support both axios and fetch-style responses

      const newBooking = {
        ...created,
        booking_id: created.booking_id || created.id,
      };

      setBooking(newBooking);
      navigate(`/booking/confirm?id=${newBooking.booking_id}`);
    } catch (err) {
      console.error("Failed to confirm booking", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to confirm booking. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (booking) {
      navigate("/success", { state: { booking } });
      return;
    }
    await createFromLegacyState();
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading booking...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        <p>{error}</p>
        <button
          onClick={() => navigate("/")}
          className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-white font-semibold"
        >
          Back home
        </button>
      </div>
    );
  }

  const hasAddons = selectedAddons && selectedAddons.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold text-slate-900">
          Booking Confirmation
        </h1>
        <p className="text-slate-600 mt-2">
          Review your booking details before continuing.
        </p>

        <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-slate-800">
            <div>
              <p className="text-sm text-slate-500">Booth</p>
              <p className="font-semibold">{boothName}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Salon</p>
              <p className="font-semibold">{salonName}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Start</p>
              <p className="font-semibold">{startTimeDisplay}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">End</p>
              <p className="font-semibold">
                {endTimeDisplay || "Calculated at booking"}
              </p>
            </div>
          </div>

          {/* Price breakdown */}
          <div className="mt-6 border-t border-slate-100 pt-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">
              Price summary
            </h2>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Base booth</span>
                <span className="font-medium">
                  {formatMoney(baseAmount)}
                </span>
              </div>
              {hasAddons && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Add-ons</span>
                  <span className="font-medium">
                    {formatMoney(addonsTotal)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-100 mt-2">
                <span className="font-semibold text-slate-800">
                  Total (including add-ons)
                </span>
                <span className="font-semibold text-slate-900">
                  {formatMoney(totalAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Add-ons detail list */}
          {hasAddons && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">
                Selected add-on amenities
              </h3>
              <ul className="space-y-1 text-sm text-slate-700">
                {selectedAddons.map((addon, idx) => {
                  const label = addon.label || addon.key;
                  const qty = addon.quantity ?? 1;
                  const lineTotal =
                    addon.line_total ??
                    addon.lineTotal ??
                    undefined;
                  return (
                    <li key={`${addon.key || idx}-${idx}`} className="flex justify-between">
                      <span>
                        {label}
                        {qty && qty !== 1 ? ` × ${qty}` : ""}
                      </span>
                      <span className="font-medium">
                        {lineTotal != null
                          ? formatMoney(lineTotal)
                          : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <button
          onClick={handleContinue}
          disabled={submitting}
          className="mt-6 w-full sm:w-auto rounded-xl bg-emerald-600 px-6 py-3 text-white font-semibold shadow-md hover:bg-emerald-700 transition disabled:opacity-60"
        >
          {booking
            ? "View confirmation"
            : submitting
            ? "Creating booking..."
            : "Confirm booking"}
        </button>
      </div>
    </div>
  );
}

export default BookingConfirm;
